import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UiState {
  isDarkMode: boolean;
  sidebarOpen: boolean;
  dateRange: {
    startDate: string | null;
    endDate: string | null;
    days: number;
  };
}

const loadDarkModePreference = (): boolean => {
  const savedPreference = localStorage.getItem('darkMode');
  if (savedPreference !== null) {
    return savedPreference === 'true';
  }
  // If no preference is saved, use system preference
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const initialState: UiState = {
  isDarkMode: loadDarkModePreference(),
  sidebarOpen: true,
  dateRange: {
    startDate: null,
    endDate: null,
    days: 30, // Default to 30 days
  },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleDarkMode: (state) => {
      state.isDarkMode = !state.isDarkMode;
      localStorage.setItem('darkMode', state.isDarkMode.toString());
    },
    setDarkMode: (state, action: PayloadAction<boolean>) => {
      state.isDarkMode = action.payload;
      localStorage.setItem('darkMode', action.payload.toString());
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    setDateRange: (state, action: PayloadAction<{ startDate?: string | null; endDate?: string | null; days?: number }>) => {
      if (action.payload.startDate !== undefined) {
        state.dateRange.startDate = action.payload.startDate;
      }
      
      if (action.payload.endDate !== undefined) {
        state.dateRange.endDate = action.payload.endDate;
      }
      
      if (action.payload.days !== undefined) {
        state.dateRange.days = action.payload.days;
        
        // If days is set, calculate start date based on end date or today
        const endDate = state.dateRange.endDate ? new Date(state.dateRange.endDate) : new Date();
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - action.payload.days);
        
        state.dateRange.startDate = startDate.toISOString().split('T')[0];
        state.dateRange.endDate = endDate.toISOString().split('T')[0];
      }
    },
  },
});

export const {
  toggleDarkMode,
  setDarkMode,
  toggleSidebar,
  setSidebarOpen,
  setDateRange,
} = uiSlice.actions;

export default uiSlice.reducer;